import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import AdminRoute from "@/components/AdminRoute";
import AdminLogin from "@/pages/AdminLogin";
import Admin from "@/pages/Admin";
import HomePage from "@/pages/HomePage";
import CanvasLanding from "@/pages/CanvasLanding";
import WallMuralsLanding from "@/pages/WallMuralsLanding";
import CustomOrderLanding from "@/pages/CustomOrderLanding";
import RequestQuote from "@/pages/RequestQuote";
import CollectionPage from "@/pages/CollectionPage";
import CanvasProductDetail from "@/pages/CanvasProductDetail";
import WallMuralProductDetail from "@/pages/WallMuralProductDetail";
import MuralCollectionPage from "@/pages/MuralCollectionPage";
import CanvasCollectionPage from "@/pages/CanvasCollectionPage";
import { CANVAS_COLLECTIONS } from "@/lib/canvasCollections";
import BannersPage from "@/pages/BannersPage";
import StickersLabelsPage from "@/pages/StickersLabelsPage";
import VehicleGraphicsPage from "@/pages/VehicleGraphicsPage";
import FloorGraphicsPage from "@/pages/FloorGraphicsPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function CanvasRouteResolver() {
  const { slug } = useParams();
  const isCollection = CANVAS_COLLECTIONS.some((c) => c.slug === slug);
  return isCollection ? <CanvasCollectionPage /> : <CanvasProductDetail />;
}

function RedirectCanvasSlug() {
  const { collectionSlug } = useParams();
  return <Navigate to={`/canvas-prints/${collectionSlug}`} replace />;
}

function ProductRedirect() {
  const { handle } = useParams();
  const [target, setTarget] = useState(null);

  useEffect(() => {
    let active = true;
    import("@/lib/api").then(({ fetchProduct }) => {
      fetchProduct(handle)
        .then((d) => {
          if (!active) return;
          const pt = (d.product?.productType || d.product?.product_type || "").toLowerCase();
          if (pt.includes("mural")) {
            setTarget(`/collections/wall-murals/${handle}`);
          } else {
            setTarget(`/canvas-prints/${handle}`);
          }
        })
        .catch(() => {
          if (active) setTarget(`/canvas-prints/${handle}`);
        });
    });
    return () => { active = false; };
  }, [handle]);

  if (target) return <Navigate to={target} replace />;
  return (
    <div className="py-24 text-center">
      <div className="inline-block w-6 h-6 border-2 border-brand-gold/30 border-t-brand-gold rounded-full animate-spin" />
    </div>
  );
}

/* Wrapper that shows storefront chrome only on non-admin routes */
function AppShell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");

  if (isAdmin) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
      </Routes>
    );
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/canvas-category-landing" element={<CanvasLanding />} />
        <Route path="/wall-murals-landing" element={<WallMuralsLanding />} />
        <Route path="/wall-murals" element={<Navigate to="/wall-murals-landing" replace />} />
        <Route path="/custom-order-landing" element={<CustomOrderLanding />} />
        <Route path="/request-quote" element={<RequestQuote />} />
        <Route
          path="/canvas-prints"
          element={
            <CollectionPage
              handle="canvas-prints"
              title="Canvas Prints"
              subtitle="Canvas Collection"
              description="Gallery-quality prints on premium stretched canvas. Choose a piece, open the detail page, and configure view, size, and frame before checkout."
              ctaLabel="Request Custom Print"
              ctaDescription="Need a bespoke canvas instead? Custom print quotes stay available as the secondary path."
              backHref="/canvas-category-landing"
              backLabel="Canvas Overview"
              productBasePath="/canvas-prints"
              cardVariant="canvas"
            />
          }
        />
        <Route
          path="/canvas-prints/:slug"
          element={<CanvasRouteResolver />}
        />
        <Route path="/stickers-labels" element={<StickersLabelsPage />} />
        <Route path="/stickers" element={<Navigate to="/stickers-labels" replace />} />
        <Route path="/banners" element={<BannersPage />} />
        <Route path="/floor-graphics" element={<FloorGraphicsPage />} />
        <Route path="/vehicle-graphics" element={<VehicleGraphicsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route
          path="/wall-murals/:collectionSlug"
          element={<MuralCollectionPage />}
        />
        {/* Redirect old /canvas/:slug to new /canvas-prints/:slug */}
        <Route
          path="/canvas/:collectionSlug"
          element={<RedirectCanvasSlug />}
        />
        <Route
          path="/collections/wall-murals"
          element={
            <CollectionPage
              handle="wall-murals"
              title="Wall Murals"
              subtitle="Mural Collection"
              description="Made-to-measure wall murals from \u00a314\u2013\u00a322 per m\u00b2 depending on material."
              ctaLabel="Get a Mural Quote"
              ctaDescription="All murals are made to measure. Send us your wall dimensions for an exact price."
              backHref="/wall-murals-landing"
              backLabel="Murals Overview"
              productBasePath="/collections/wall-murals"
              cardVariant="mural"
            />
          }
        />
        <Route
          path="/collections/wall-murals/:handle"
          element={<WallMuralProductDetail />}
        />
        {/* Catch Shopify /products/:handle URLs and redirect to storefront */}
        <Route
          path="/products/:handle"
          element={<ProductRedirect />}
        />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <HelmetProvider>
    <AdminAuthProvider>
    <BrowserRouter>
      <ScrollToTop />
      <AppShell />
    </BrowserRouter>
    </AdminAuthProvider>
    </HelmetProvider>
  );
}

export default App;
